import type { SequenceData } from '../types';

/**
 * Single source of truth for liability severity display config.
 * Used by the LiabilityColumnHeader popover legend, the inline badge cell renderer in
 * InnerChildSequenceTable, and via `color` which is the clonotype network map's "Color by
 * liabilities" mode and its legend.
 */
export const LIABILITY_SEVERITY = [
  { key: 'critical', label: 'Critical', score: '-2.0', statuses: ['Critical'], cls: 'ring-red-600 bg-red-50 text-red-700', color: '#dc2626' },
  { key: 'strong', label: 'Strong', score: '-1.0', statuses: ['Strong'], cls: 'ring-orange-500 bg-orange-50 text-orange-700', color: '#f97316' },
  { key: 'mild', label: 'Mild', score: '-0.5', statuses: ['Mild'], cls: 'ring-yellow-600 bg-yellow-50 text-yellow-700', color: '#ca8a04' },
  { key: 'none', label: 'None/Weak', score: '0.0', statuses: ['None', 'Weak'], cls: 'ring-green-600 bg-green-50 text-green-700', color: '#16a34a' },
] as const;

export type LiabilitySeverityKey = (typeof LIABILITY_SEVERITY)[number]['key'];

/** Quick lookup by severity key — avoids repeated array.find() at render time */
export const LIABILITY_BADGE = Object.fromEntries(LIABILITY_SEVERITY.map((l) => [l.key, l])) as Record<
  LiabilitySeverityKey,
  (typeof LIABILITY_SEVERITY)[number]
>;

/**
 * Returns the highest-severity badge config for a given sequence.
 *
 * Uses metadata.liabilityScore — the backend already resolves the worst-case
 * score across both chains, so there is no need to re-parse the raw liability
 * strings here. Score thresholds match the backend severity buckets:
 *   critical ≤ -2.0 | strong ≤ -1.0 | mild < 0 | none/weak ≥ 0
 */
export function getLiabilityBadge(sequence: SequenceData): (typeof LIABILITY_SEVERITY)[number] {
  const score = sequence.metadata.liabilityScore ?? 0;
  if (score <= -2.0) {
    return LIABILITY_BADGE.critical;
  }
  if (score <= -1.0) {
    return LIABILITY_BADGE.strong;
  }
  if (score < 0) {
    return LIABILITY_BADGE.mild;
  }
  return LIABILITY_BADGE.none;
}
